import { z } from 'zod/v4';
import { ActionResourceSchema } from './action-resource.js';

export const ActionDetailParamsSchema = z.object({
  id: z.string().min(1, 'Action ID is required'),
});

export type ActionDetailParams = z.infer<typeof ActionDetailParamsSchema>;

/**
 * One action on the `GET /api/capa/actions/:id` wire: every field of
 * `ActionResourceSchema` plus the caller's roles on that action.
 *
 * `userRoles` / `companyRoles` are caller-relative — two companies looking at
 * the same action see different values — and both dashboards gate their edit
 * controls on them, so they are part of the contract rather than a convenience.
 * They are derived in `deriveActionRoles` from the link rows the read already
 * returns.
 *
 * Wire-format notes (do not "fix" — both dashboards read this shape):
 * - Dates are `Date` objects here and ISO strings on the wire; the route's
 *   serializer converts them. `remediationRequest` and `linkedActions` carry
 *   jsonb-sourced timestamps that the transformer coerces to `Date` for
 *   exactly that reason.
 * - `isArchived` is the raw `0`/`1` integer, and it means "archived by *your*
 *   company" — per-viewer membership in the action-archive table, not the
 *   legacy `is_archived` column.
 * - `cap.status` is a live rollup over the child actions this caller can see,
 *   never the stored parent status.
 * - This is a plain object schema, so an undeclared key is stripped rather
 *   than passed through. Adding a column to the query means declaring it here;
 *   the integration test asserts the key set both ways.
 */
export const ActionDetailSchema = ActionResourceSchema.extend({
  userRoles: z.array(z.string()),
  companyRoles: z.array(z.string()),
  // Fields the resource schema leaves optional because the *list* read does not
  // select them. The detail read always does — its SQL coalesces each of these
  // to an empty array or object — so they are required here, and the key-set
  // integration assertion proves it against a real response.
  owner: ActionResourceSchema.shape.owner.unwrap(),
  source: ActionResourceSchema.shape.source.unwrap(),
  tags: ActionResourceSchema.shape.tags.unwrap(),
  cap: ActionResourceSchema.shape.cap.unwrap(),
  users: ActionResourceSchema.shape.users.unwrap(),
  companies: ActionResourceSchema.shape.companies.unwrap(),
  linkedActions: ActionResourceSchema.shape.linkedActions.unwrap(),
  remediationRequest: ActionResourceSchema.shape.remediationRequest.unwrap(),
  createdByCompanyName:
    ActionResourceSchema.shape.createdByCompanyName.unwrap(),
  internalCompanyCode: ActionResourceSchema.shape.internalCompanyCode.unwrap(),
  notes: ActionResourceSchema.shape.notes.unwrap(),
  generatedBy: ActionResourceSchema.shape.generatedBy.unwrap(),
});

export type ActionDetail = z.infer<typeof ActionDetailSchema>;
